import { formatAmount } from "@loot-os/api-contract";
import { buttonVariants } from "@loot-os/ui/components/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import {
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@loot-os/ui/components/chart";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { Area, Bar, ComposedChart, XAxis, YAxis } from "recharts";

import { amountColumnHeader, capitalize, useCurrency } from "../lib/currency";
import { formatDate, formatNumber } from "../lib/format";
import { useTRPC } from "../lib/trpc";
import { usePlatformSwitchNotice } from "../lib/usePlatformSwitchNotice";

const activityChartConfig = {
  experiences: { label: "Loot experiences", color: "var(--chart-1)" },
  revenue: { label: "Revenue (USD)", color: "var(--chart-2)" },
} satisfies ChartConfig;

/** "2026-08-21" → "Aug 21"; pinned to UTC so the day never shifts with the viewer's tz. */
function formatChartDay(date: string): string {
  return new Date(`${date}T00:00:00Z`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

/** Shared minimal x-axis: no line, no ticks, just the first and last date. */
function edgeTicks(data: { date: string }[]): string[] | undefined {
  const first = data.at(0);
  const last = data.at(-1);
  if (!first || !last || first === last) return undefined;
  return [first.date, last.date];
}

function ActivityChart() {
  const trpc = useTRPC();
  const timeseries = useQuery(trpc.stats.timeseries.queryOptions());

  const data = (timeseries.data ?? []).map((point) => ({
    date: point.date,
    experiences: point.experiences,
    revenue: point.revenueCents / 100,
  }));

  // The revenue domain is padded so bars top out in the bottom third of the
  // chart, keeping them visually beneath the experiences line's peaks.
  const revenueMax = Math.max(1, ...data.map((d) => d.revenue));
  // Floored at 1 so a month with no opens draws its line on the baseline, not across the middle.
  const experiencesMax = Math.max(1, ...data.map((d) => d.experiences));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Activity (30d)</CardTitle>
      </CardHeader>
      <CardContent>
        <ChartContainer config={activityChartConfig} className="h-64 w-full">
          <ComposedChart data={data} margin={{ left: 0, right: 0, top: 4 }}>
            <defs>
              <linearGradient id="fillExperiences" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--color-experiences)" stopOpacity={0.3} />
                <stop offset="100%" stopColor="var(--color-experiences)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              ticks={edgeTicks(data)}
              // Both edge labels stay: the default drops a first tick that overhangs the plot.
              interval="preserveStartEnd"
              tickFormatter={formatChartDay}
              className="text-[10px]"
            />
            <YAxis yAxisId="experiences" hide domain={[0, experiencesMax]} />
            <YAxis yAxisId="revenue" hide domain={[0, revenueMax * 3]} />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  labelFormatter={(label) =>
                    typeof label === "string" ? formatChartDay(label) : label
                  }
                  formatter={(value, name) => (
                    <>
                      <div
                        className="h-2 w-2 shrink-0 rounded-[2px]"
                        style={{ backgroundColor: `var(--color-${name})` }}
                      />
                      {activityChartConfig[name as keyof typeof activityChartConfig]?.label ?? name}
                      <div className="ml-auto pl-4 font-mono font-medium tabular-nums text-foreground">
                        {name === "revenue"
                          ? `$${Number(value).toFixed(2)}`
                          : formatNumber(Number(value))}
                      </div>
                    </>
                  )}
                />
              }
            />
            <ChartLegend content={<ChartLegendContent />} />
            <Bar
              yAxisId="revenue"
              dataKey="revenue"
              fill="var(--color-revenue)"
              radius={[4, 4, 0, 0]}
              maxBarSize={16}
            />
            <Area
              yAxisId="experiences"
              dataKey="experiences"
              type="monotone"
              stroke="var(--color-experiences)"
              strokeWidth={2}
              fill="url(#fillExperiences)"
              dot={false}
            />
          </ComposedChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <Card className="gap-1 py-4">
      <CardHeader className="px-4">
        <CardDescription>{label}</CardDescription>
      </CardHeader>
      <CardContent className="px-4 text-2xl font-semibold tabular-nums">{value}</CardContent>
    </Card>
  );
}

export function OverviewPage() {
  const trpc = useTRPC();
  const { tenantId } = useParams({ strict: false }) as { tenantId: string };
  const stats = useQuery(trpc.stats.overview.queryOptions());
  const opens = useQuery(trpc.opens.list.queryOptions({ limit: 10 }));
  // The currency is per storefront, and the stats below are this environment's — so both
  // read the same environment.
  const currency = useCurrency();
  // A platform-wide halt explains an empty chart better than the chart can.
  usePlatformSwitchNotice();

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Overview</h1>
        <p className="text-sm text-muted-foreground">
          What's happening across this tenant's boxes.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
        <StatCard
          label="Opens today"
          value={stats.data ? formatNumber(stats.data.opensToday) : "—"}
        />
        <StatCard
          label="Opens (30d)"
          value={stats.data ? formatNumber(stats.data.opens30d) : "—"}
        />
        <StatCard
          label="End users"
          value={stats.data ? formatNumber(stats.data.endUserCount) : "—"}
        />
        <StatCard
          label={`${capitalize(currency.namePlural)} outstanding`}
          value={stats.data ? formatAmount(stats.data.balanceOutstandingCents, currency) : "—"}
        />
      </div>

      <ActivityChart />

      <Card>
        <CardHeader>
          <CardTitle>Recent opens</CardTitle>
          <CardAction>
            <Link
              to="/tenants/$tenantId/users"
              params={{ tenantId }}
              className={buttonVariants({ variant: "ghost", size: "sm" })}
            >
              View users
              <ArrowRight />
            </Link>
          </CardAction>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>Item</TableHead>
                <TableHead className="text-right">
                  {amountColumnHeader("Value", currency)}
                </TableHead>
                <TableHead className="text-right">{amountColumnHeader("Cost", currency)}</TableHead>
                <TableHead>End user</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {opens.data?.data.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                    No opens yet — embed the opener or hit the public API to see activity here.
                  </TableCell>
                </TableRow>
              )}
              {opens.data?.data.map((open) => (
                <TableRow key={open.id}>
                  <TableCell className="text-muted-foreground">
                    {formatDate(open.createdAt)}
                  </TableCell>
                  <TableCell>{open.item.name}</TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {formatAmount(open.item.value, currency)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {formatAmount(open.costCents, currency)}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {open.endUserId}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
